<template>
	<div v-show="active" class="lcsj-lock-screen" role="dialog" aria-modal="true" aria-labelledby="lock-title" @keydown="trapFocus">
		<div class="lock-brand"><span class="lock-brand__mark">六</span>六彩视界 <span>门店订单管理系统</span></div>
		<div class="lock-content">
			<div class="lock-clock"><time>{{ time }}</time><div>{{ date }}<span>{{ weekday }}</span></div></div>
			<section class="lock-card">
				<div class="lock-icon"><i class="el-icon-lock"></i></div>
				<h1 id="lock-title">{{ state.locked ? '系统已锁定' : '设置本次锁屏密码' }}</h1>
				<p>{{ state.locked ? userName + '，输入锁屏密码继续工作' : '设置 6–32 位密码，确认后锁定当前界面' }}</p>
				<form @submit.prevent="unlock">
					<label for="system-lock-password">锁屏密码</label>
					<input id="system-lock-password" ref="password" v-model="password" type="password" :autocomplete="state.locked ? 'current-password' : 'new-password'" maxlength="32" placeholder="请输入锁屏密码" :aria-invalid="!!error" aria-describedby="lock-error" :disabled="busy" @input="error = ''" />
					<template v-if="!state.locked"><label class="lock-confirm-label" for="system-lock-confirm">确认锁屏密码</label><input id="system-lock-confirm" v-model="confirmation" type="password" autocomplete="new-password" maxlength="32" placeholder="请再次输入锁屏密码" :disabled="busy" @input="error = ''" /></template>
					<div id="lock-error" class="lock-error" role="alert">{{ remaining ? '请在 ' + remaining + ' 秒后重试' : error }}</div>
					<button class="lock-submit" type="submit" :disabled="busy || !!remaining">{{ busy ? '请稍候…' : state.locked ? '解锁并继续' : '确认并锁定' }}<i class="el-icon-right"></i></button>
				</form>
				<button v-if="state.locked" class="lock-login" type="button" @click="returnToLogin"><i class="el-icon-switch-button"></i> 返回登录</button>
				<button v-else class="lock-login" type="button" @click="cancel">取消锁屏</button>
			</section>
			<p class="lock-note"><i class="el-icon-circle-check"></i> 当前工作界面已保留，解锁后继续操作</p>
		</div>
		<div class="lock-shortcut">快速锁屏 <kbd>Alt</kbd> + <kbd>L</kbd></div>
	</div>
</template>

<script>
import { leaveOnlineSession } from "@/utils/onlinePresence";
import { lockState, requestSystemLock, cancelSystemLock, lockSystem, unlockSystem, clearSystemLock } from "@/utils/systemLock";

export default {
	name: "SystemLock",
	data: () => ({ state: lockState, password: "", confirmation: "", error: "", busy: false, now: new Date(), userName: "" }),
	computed: {
		active() { return this.state.locked || this.state.preparing; },
		time() { return this.now.toLocaleTimeString("zh-CN", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" }); },
		date() { return this.now.toLocaleDateString("zh-CN", { year: "numeric", month: "long", day: "numeric" }); },
		weekday() { return ["星期日", "星期一", "星期二", "星期三", "星期四", "星期五", "星期六"][this.now.getDay()]; },
		remaining() { return Math.max(0, Math.ceil((this.state.retryAt - this.now.getTime()) / 1000)); }
	},
	watch: {
		active(active) { this.updateLock(active); },
		"state.locked"() { this.password = ""; this.confirmation = ""; this.error = ""; this.$nextTick(() => { if (this.active) this.$refs.password.focus(); }); }
	},
	mounted() {
		// Escape app stacking contexts and cover Element UI's append-to-body dialogs as well.
		document.body.appendChild(this.$el);
		window.addEventListener("keydown", this.onKeydown, true);
		document.addEventListener("focusin", this.onFocus, true);
		this.updateLock(this.active);
	},
	beforeDestroy() {
		window.removeEventListener("keydown", this.onKeydown, true);
		document.removeEventListener("focusin", this.onFocus, true);
		this.updateLock(false);
		if (this.$el.parentNode) this.$el.parentNode.removeChild(this.$el);
	},
	methods: {
		updateLock(locked) {
			clearInterval(this.timer);
			document.body.classList.toggle("is-system-locked", locked);
			this.password = "";
			this.confirmation = "";
			this.error = "";
			if (locked) {
				this.previousFocus = document.activeElement;
				try { this.userName = JSON.parse(localStorage.getItem("userdata")).userInfo.chName || "您好"; } catch (_) { this.userName = "您好"; }
				this.now = new Date();
				this.timer = setInterval(() => { this.now = new Date(); }, 1000);
				this.$nextTick(() => this.$refs.password.focus());
			} else if (this.previousFocus && this.previousFocus.isConnected) this.previousFocus.focus();
		},
		onKeydown(event) {
			if (event.altKey && !event.ctrlKey && !event.metaKey && event.key.toLowerCase() === "l") {
				if (this.$route.path === "/" || this.$route.path === "/login") return;
				event.preventDefault();
				event.stopImmediatePropagation();
				if (!event.repeat) {
					try { requestSystemLock(); } catch (_) { this.$message.error("无法保存锁屏状态，请检查浏览器存储设置"); }
				}
			} else if (this.active) {
				if (event.key === "Escape" && !this.state.locked) this.cancel();
				// Stop business shortcuts while still allowing typing, submission and focus in the lock form.
				if (!this.$el.contains(event.target) || event.key === "Escape") {
					event.preventDefault();
					event.stopImmediatePropagation();
				}
			}
		},
		onFocus(event) { if (this.active && !this.$el.contains(event.target)) this.$refs.password.focus(); },
		trapFocus(event) {
			event.stopPropagation();
			if (event.key !== "Tab") return;
			const nodes = Array.from(this.$el.querySelectorAll("input:not(:disabled),button:not(:disabled)"));
			const first = nodes[0], last = nodes[nodes.length - 1];
			if (event.shiftKey && event.target === first) { event.preventDefault(); last.focus(); }
			if (!event.shiftKey && event.target === last) { event.preventDefault(); first.focus(); }
		},
		async unlock() {
			if (this.busy || this.remaining) return;
			if (!this.password) { this.error = "请输入锁屏密码"; this.$refs.password.focus(); return; }
			if (!this.state.locked && this.password !== this.confirmation) { this.error = "两次输入的锁屏密码不一致"; return; }
			this.busy = true;
			try {
				if (this.state.locked) await unlockSystem(this.password);
				else await lockSystem(this.password);
			}
			catch (error) { this.error = error.message; this.password = ""; this.$refs.password.focus(); }
			finally { this.busy = false; this.$nextTick(() => { if (this.active) this.$refs.password.focus(); }); }
		},
		cancel() { cancelSystemLock(); },
		async returnToLogin() {
			await leaveOnlineSession();
			this.$permissionStore.commit("logout", "false");
			["userdata", "logintoken", "userId", "userInfo"].forEach(key => localStorage.removeItem(key));
			sessionStorage.removeItem("accessToken");
			clearSystemLock();
			// Reload also discards cached tabs, drafts and appended business dialogs for the next login.
			this.$router.replace("/login").then(() => window.location.reload());
		}
	}
};
</script>

<style>
body.is-system-locked { overflow: hidden !important; }
body.is-system-locked > :not(.lcsj-lock-screen) { visibility: hidden !important; pointer-events: none !important; }
/* Some poppers set visibility explicitly, so include their descendants. */
body.is-system-locked > :not(.lcsj-lock-screen) * { visibility: hidden !important; }
.lcsj-lock-screen { position: fixed; inset: 0; z-index: 2147483647; overflow: auto; box-sizing: border-box; color: var(--app-text); background: radial-gradient(ellipse at 15% 10%, var(--app-primary-soft-hover), transparent 55%), radial-gradient(ellipse at 90% 85%, var(--app-primary-soft), transparent 50%), var(--app-page-bg); font-family: "Microsoft YaHei", sans-serif; padding: 32px 48px; }
.lock-brand { display: flex; align-items: center; gap: 12px; font-size: 18px; font-weight: 600; }
.lock-brand > span:last-child { font-size: 12px; color: var(--app-text-secondary); font-weight: 400; border-left: 1px solid var(--app-border-strong); padding-left: 12px; }
.lock-brand__mark { display: grid; place-items: center; width: 32px; height: 32px; border-radius: 10px; background: var(--app-primary); color: var(--app-primary-contrast); }
.lock-content { width: 400px; max-width: 100%; margin: 5vh auto 24px; text-align: center; }
.lock-clock { margin-bottom: 28px; }
.lock-clock time { display: block; font-size: clamp(44px, 6vw, 76px); line-height: 1.3; font-weight: 300; letter-spacing: 3px; font-variant-numeric: tabular-nums; }
.lock-clock > div { margin-top: 8px; color: var(--app-text-secondary); font-size: 15px; }
.lock-clock span { margin-left: 18px; }
.lock-card { padding: 28px 32px 22px; background: var(--app-surface); border: 1px solid var(--app-border); border-radius: 20px; box-shadow: var(--app-shadow); }
.lock-icon { display: grid; place-items: center; margin: 0 auto 16px; width: 52px; height: 52px; border-radius: 16px; background: var(--app-primary-soft); color: var(--app-primary-text); font-size: 26px; }
.lock-card h1 { margin: 0; font-size: 22px; }
.lock-card p { margin: 10px 0 26px; font-size: 13px; color: var(--app-text-secondary); }
.lock-card form { text-align: left; }
.lock-card label { display: block; font-size: 13px; margin-bottom: 8px; }
.lock-card .lock-confirm-label { margin-top: 14px; }
.lock-card input { width: 100%; height: 46px; padding: 0 14px; box-sizing: border-box; border: 1px solid var(--app-border-strong); border-radius: 8px; background: var(--app-surface-sunken); color: var(--app-text); font-size: 14px; }
.lock-card input:focus { outline: 2px solid var(--app-primary-border); border-color: var(--app-primary); }
.lock-error { min-height: 30px; padding-top: 6px; box-sizing: border-box; color: var(--app-danger, #e05252); font-size: 12px; }
.lock-submit { width: 100%; height: 44px; border: 0; border-radius: 8px; background: var(--app-primary); color: var(--app-primary-contrast); font-size: 14px; font-weight: 600; cursor: pointer; }
.lock-submit i { margin-left: 10px; }
.lock-submit:hover:not(:disabled) { background: var(--app-button-hover); box-shadow: 0 0 0 3px var(--app-primary-border); }
.lock-submit:disabled { opacity: .6; cursor: wait; }
.lock-login { margin-top: 20px; border: 0; padding: 6px 12px; border-radius: 6px; background: transparent; color: var(--app-text-secondary); cursor: pointer; }
.lock-login:hover { color: var(--app-primary-text); background: var(--app-primary-soft); }
.lock-note { color: var(--app-text-secondary); font-size: 12px; margin-top: 20px; }
.lock-shortcut { text-align: center; color: var(--app-text-secondary); font-size: 12px; }
.lock-shortcut kbd { display: inline-block; margin-left: 6px; padding: 4px 7px; border: 1px solid var(--app-border-strong); border-radius: 5px; background: var(--app-surface); }
@media (max-height: 800px) {
	.lcsj-lock-screen { padding: 24px 32px; }
	.lock-content { margin: 16px auto; }
	.lock-clock { margin-bottom: 20px; }
	.lock-clock time { font-size: 56px; }
	.lock-card { padding: 22px 32px 18px; }
	.lock-icon { width: 42px; height: 42px; font-size: 23px; margin-bottom: 12px; }
	.lock-card p { margin-bottom: 20px; }
	.lock-note { margin-top: 16px; }
}
@media (max-width: 600px) { .lcsj-lock-screen { padding: 20px; } .lock-brand > span:last-child { display: none; } .lock-card { padding: 24px; } .lock-content { margin-top: 32px; } }
</style>
